import { RestEndpointMethodTypes } from "@octokit/plugin-rest-endpoint-methods";
import { Octokit } from "@octokit/rest";
import { Endpoints } from "@octokit/types";
import get from "lodash/get";
import omit from "lodash/omit";
import React from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { GITHUB_WORKFLOW_RUN_STATUSES } from "Types/githubWorkflowRuns";

const octokit = new Octokit({
  auth: process.env.REACT_APP_GH_API_PAT,
});

const WORKFLOW_RUN_KEY = "workflow_runs";

type WithOptionalOwner<T> = Omit<T, "owner"> & {
  owner?: string;
};

export const useDeploymentList = (
  deploymentParams: WithOptionalOwner<
    Endpoints["GET /repos/{owner}/{repo}/deployments"]["parameters"]
  >[]
) => {
  return useQueries(
    deploymentParams.map((params) => ({
      queryKey: ["deployments_list", params],
      queryFn: () =>
        octokit.rest.repos.listDeployments({
          owner: params.owner || "leanspace",
          per_page: 1,
          ...omit(params, "owner"),
        }),
    }))
  );
};

export const useDeploymentStatuses = (
  deploymentParams: WithOptionalOwner<
    Endpoints["GET /repos/{owner}/{repo}/deployments"]["parameters"]
  >[]
) => {
  const deploymentsLists = useDeploymentList(deploymentParams);

  const deploymentIds = deploymentsLists.map((deploymentsResult) =>
    get(deploymentsResult, "data.data.[0].id", null)
  );

  return useQueries(
    deploymentIds.map((deployment_id, index) => ({
      queryKey: ["deployment_status", deployment_id],
      queryFn: () =>
        octokit.rest.repos.listDeploymentStatuses({
          deployment_id: Number(deployment_id),
          owner: deploymentParams[index].owner || "leanspace",
          repo: deploymentParams[index].repo,
          per_page: 1,
        }),
      enabled: Boolean(deployment_id),
    }))
  );
};

export const useReleases = (
  params: WithOptionalOwner<
    Endpoints["GET /repos/{owner}/{repo}/releases"]["parameters"]
  >
) => {
  return useQuery(["releases", params], () =>
    octokit.rest.repos.listReleases({
      owner: params.owner || "leanspace",
      ...omit(params, "owner"),
    })
  );
};

const findMostRecentWorkflowRunId = (
  workflowList:
    | RestEndpointMethodTypes["actions"]["listWorkflowRunsForRepo"]["response"]
    | undefined,
  workflowId: string
) => {
  if (!workflowList) {
    return undefined;
  }

  const workflowRuns = workflowList.data.workflow_runs.filter((wf) =>
    wf.path.includes(workflowId)
  );

  if (workflowRuns.length === 0) {
    return undefined;
  }

  const sortedWorkflowRuns = [...workflowRuns].sort(
    (a, b) =>
      Date.parse(b.run_started_at ?? "") - Date.parse(a.run_started_at ?? "")
  );

  return sortedWorkflowRuns[0].id;
};

export const useTriggerWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      params: WithOptionalOwner<
        Endpoints["POST /repos/{owner}/{repo}/actions/workflows/{workflow_id}/dispatches"]["parameters"]
      >
    ) => {
      return octokit.rest.actions.createWorkflowDispatch({
        owner: params.owner || "leanspace",
        ...omit(params, "owner"),
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_RUN_KEY] }),
  });
};

export const useTriggerWorkflowWithRunIdPoll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: Omit<
        Endpoints["POST /repos/{owner}/{repo}/actions/workflows/{workflow_id}/dispatches"]["parameters"],
        "owner"
      > & { owner?: string }
    ) => {
      const { repo, workflow_id, owner, ...rest } = params;
      const actualOwner = owner || "leanspace";

      // Get initial run ID before triggering
      const runsBefore = await octokit.rest.actions.listWorkflowRunsForRepo({
        owner: actualOwner,
        repo,
        path: `.github/workflows/${workflow_id}`,
      });
      const initialRunId = findMostRecentWorkflowRunId(
        runsBefore,
        String(workflow_id)
      );

      // Trigger the workflow
      await octokit.rest.actions.createWorkflowDispatch({
        owner: actualOwner,
        repo,
        workflow_id,
        ...rest, // contains ref + inputs
      });

      queryClient.invalidateQueries({
        queryKey: [WORKFLOW_RUN_KEY, repo, String(workflow_id)],
      });

      // Poll until new run ID appears
      let newRunId: number | undefined;
      for (let i = 0; i < 20; i++) {
        // ~100s max wait
        const runs = await octokit.rest.actions.listWorkflowRunsForRepo({
          owner: actualOwner,
          repo,
          path: `.github/workflows/${workflow_id}`,
        });

        const id = findMostRecentWorkflowRunId(runs, String(workflow_id));
        if (id && id !== initialRunId) {
          newRunId = id;
          break;
        }

        await new Promise((res) => setTimeout(res, 5000));
      }

      if (!newRunId) {
        throw new Error("Timed out waiting for new workflow run");
      }

      return newRunId;
    },
  });
};

export const useFileContent = (repo: string, path: string) => {
  return useQuery(["manifest", repo, path], async () => {
    const response = await octokit.rest.repos.getContent({
      owner: "leanspace",
      repo,
      path,
    });

    const content = get(response, "data.content", null);
    return content ? atob(content) : null;
  });
};

export const useWorkflowRuns = (
  repo: string,
  workflowId: string,
  polling: boolean
) => {
  const query = useQuery({
    queryKey: [WORKFLOW_RUN_KEY, repo, workflowId],
    queryFn: () =>
      octokit.rest.actions.listWorkflowRuns({
        owner: "leanspace",
        repo,
        workflow_id: workflowId,
      }),
    refetchInterval: polling ? 5000 : false,
  });

  const latestRunId = React.useMemo(() => {
    const runs = query.data?.data?.workflow_runs;
    if (!runs || runs.length === 0) return undefined;
    const latest = runs.reduce((prev, curr) =>
      Date.parse(curr.run_started_at ?? "") >
      Date.parse(prev.run_started_at ?? "")
        ? curr
        : prev
    );
    return latest.id;
  }, [query.data]);

  return { ...query, latestRunId };
};

export const usePollWorkflowRunUntilCompleted = (
  repo: string,
  workflowRunId: number | undefined
) => {
  return useQuery(
    [WORKFLOW_RUN_KEY, repo, workflowRunId],
    async () => {
      return await octokit.rest.actions.getWorkflowRun({
        owner: "leanspace",
        repo: repo,
        run_id: workflowRunId as number, // protected by 'enabled'
      });
    },
    {
      enabled: Boolean(workflowRunId),
      refetchInterval: (run) =>
        [
          GITHUB_WORKFLOW_RUN_STATUSES.success,
          GITHUB_WORKFLOW_RUN_STATUSES.failure,
          GITHUB_WORKFLOW_RUN_STATUSES.completed,
        ].indexOf(run?.data?.status as GITHUB_WORKFLOW_RUN_STATUSES) === -1
          ? 5000
          : false,
    }
  );
};
